import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCalendar, FaFileSignature, FaClock, FaMapMarkerAlt, FaUsers, FaShieldAlt, FaDollarSign, FaHandshake } from "react-icons/fa";
import { useToast } from "../../Context/ToastContext";
import { useContext } from "react";
import { AuthContext } from "../../Context/AuthContext";
import useReadCharterRequest from "../lib/useReadCharterRequest";
import useIsChartererMovingForward from "../lib/useIsChartererMovingForward";
import useSelectMeetingDate from "../lib/useSelectMeetingDate";
import useSignVesselNominationAgreement from "../lib/useSignVesselNominationAgreement";
import useSignCharterPartyAgreement from "../lib/useSignCharterPartyAgreement";
import Skeleton from "react-loading-skeleton";
import WorkflowSteps from "../../Utils/WorkflowSteps";
import { calculateWorkflowStep, getNextAction } from "../../Utils/workflowStepCalculator";
import VesselNominationAgreementModal from "../components/VesselNominationAgreementModal";
import CharterPartyAgreementModal from "../components/CharterPartyAgreementModal";
import "./CharterRequestDetails.css";

const CharterRequestDetails = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { profileData } = useContext(AuthContext);

  // Modal states
  const [showMoveForwardModal, setShowMoveForwardModal] = useState(false);
  const [showMeetingDateModal, setShowMeetingDateModal] = useState(false);
  const [showSignVesselModal, setShowSignVesselModal] = useState(false);
  const [showSignCharterPartyModal, setShowSignCharterPartyModal] = useState(false);

  // Form states
  const [meetingDates, setMeetingDates] = useState({
    date1: "",
    date2: ""
  });

  const [moveForwardForm, setMoveForwardForm] = useState({
    processed: true,
    acceptPrice: true,
    userMessage: ""
  });

  // API hooks
  const { charterRequest, charterRequestLoading, charterRequestError } = useReadCharterRequest(requestId);
  const { moveForward, isLoading: moveForwardLoading, isSuccess: moveForwardSuccess, isError: moveForwardError, error: moveForwardErrorData } = useIsChartererMovingForward();
  const { selectMeetingDate, isLoading: meetingDateLoading, isSuccess: meetingDateSuccess, isError: meetingDateError, error: meetingDateErrorData } = useSelectMeetingDate();
  const { signAgreement: signVesselAgreement, isLoading: signAgreementLoading, isSuccess: signAgreementSuccess, isError: signAgreementError, error: signAgreementErrorData } = useSignVesselNominationAgreement();
  const { signAgreement: signCharterPartyAgreement, isLoading: signCharterPartyAgreementLoading, isSuccess: signCharterPartyAgreementSuccess, isError: signCharterPartyAgreementError, error: signCharterPartyAgreementErrorData } = useSignCharterPartyAgreement();

  // Calculate current step and next action
  const currentStep = charterRequest ? calculateWorkflowStep(charterRequest) : 1;
  const nextAction = getNextAction(currentStep, "charterer");

  // Map API step to display step (12-step workflow)
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest => charterer
      case 2: return 2; // processCharterRequest => admin
      case 3: return 3; // charterResponse => vessel owner
      case 4: return 4; // processCharterResponse => admin
      case 5: return 5; // isCharterMovingForward => charterer
      case 6: return 6; // processCharterMovingForward => admin (if acceptPrice false)
      case 7: return 7; // selectMeetingDate => charterer and vessel owner
      case 8: return 8; // meeting result saved to database => system
      case 9: return 9; // signVesselNominationAgreement => charterer and vessel owner
      case 10: return 10; // processVesselInspectionResult => admin
      case 11: return 11; // signCharterPartyAgreement => charterer and vessel owner
      case 12: return 12; // processCharterPartyAgreement => admin
      default: return apiStep;
    }
  };

  const displayStep = getDisplayStep(currentStep);

  // Debug logging
  console.log("Charterer Dashboard - Charter Request:", charterRequest);
  console.log("Charterer Dashboard - Current Step:", currentStep);
  console.log("Charterer Dashboard - Display Step:", displayStep);
  console.log("Charterer Dashboard - Next Action:", nextAction);
  console.log("Charterer Dashboard - API Step:", charterRequest?.step);
  console.log("Charterer Dashboard - Vessel Inspection Data:", charterRequest?.vesselInspected);
  console.log("Charterer Dashboard - Should show charter party button:", displayStep === 10);

  // Helper functions
  const resetMoveForwardForm = useCallback(() => {
    setMoveForwardForm({
      processed: true,
      acceptPrice: true,
      userMessage: ""
    });
  }, []);

  // Handle success and error states
  useEffect(() => {
    if (moveForwardSuccess) {
      showToast("Charter request updated successfully!", "success");
      resetMoveForwardForm();
      setShowMoveForwardModal(false);
    }
  }, [moveForwardSuccess, showToast, resetMoveForwardForm, setShowMoveForwardModal]);

  useEffect(() => {
    if (moveForwardError && moveForwardErrorData) {
      showToast(moveForwardErrorData.message || "Failed to update charter request", "error");
    }
  }, [moveForwardError, moveForwardErrorData, showToast]);

  useEffect(() => {
    if (meetingDateSuccess) {
      showToast("Meeting dates selected successfully!", "success");
      setShowMeetingDateModal(false);
      setMeetingDates({ date1: "", date2: "" });
    }
  }, [meetingDateSuccess, showToast, setShowMeetingDateModal, setMeetingDates]);

  useEffect(() => {
    if (meetingDateError && meetingDateErrorData) {
      showToast(meetingDateErrorData.message || "Failed to select meeting dates", "error");
    }
  }, [meetingDateError, meetingDateErrorData, showToast]);

  useEffect(() => {
    if (signAgreementSuccess) {
      showToast("Vessel nomination agreement signed successfully!", "success");
      setShowSignVesselModal(false);
    }
  }, [signAgreementSuccess, showToast, setShowSignVesselModal]);

  useEffect(() => {
    if (signAgreementError && signAgreementErrorData) {
      showToast(signAgreementErrorData.message || "Failed to sign agreement", "error");
    }
  }, [signAgreementError, signAgreementErrorData, showToast]);

  useEffect(() => {
    if (signCharterPartyAgreementSuccess) {
      showToast("Charter party agreement signed successfully!", "success");
      setShowSignCharterPartyModal(false); // Close modal after successful signing
    }
  }, [signCharterPartyAgreementSuccess, showToast, setShowSignCharterPartyModal]);

  useEffect(() => {
    if (signCharterPartyAgreementError && signCharterPartyAgreementErrorData) {
      showToast(signCharterPartyAgreementErrorData.message || "Failed to sign charter party agreement", "error");
    }
  }, [signCharterPartyAgreementError, signCharterPartyAgreementErrorData, showToast]);

  // Helper functions
  const getStatusClass = (status) => {
    switch(status) {
      case "pending": return "pending";
      case "approved": return "approved";
      case "in-use": return "in-use";
      case "declined": return "declined";
      case "In Review": return "in-review";
      case "Pending": return "pending";
      case "Approved": return "approved";
      case "Charter Request Created": return "pending";
      case "Admin Processing": return "in-review";
      case "Vessel Owner Response Required": return "in-review";
      case "Admin Review": return "in-review";
      case "Charterer Decision": return "in-review";
      case "Select Meeting Dates": return "approved";
      case "Meeting Dates Saved": return "approved";
      case "Sign Vessel Nomination": return "approved";
      case "Processing Inspection": return "in-review";
      case "Sign Charter Party": return "approved";
             case "Completed": return "approved";
      default: return "pending";
    }
  };

  // Determine status based on step and other fields
  const getRequestStatus = (charterRequest) => {
    if (!charterRequest) return "Pending";
    
    const step = charterRequest.step;
    
    switch (step) {
      case 1:
        return "Charter Request Created"; // Charterer created request
      case 2:
        return "Admin Processing"; // Admin processing charter request
      case 3:
        return "Vessel Owner Response Required"; // Vessel owner needs to respond
      case 4:
        return "Admin Review"; // Admin reviewing vessel owner response
      case 5:
        return "Charterer Decision"; // Charterer deciding
      case 6:
        return "Admin Processing"; // Admin processing charterer decision
      case 7:
        return "Select Meeting Dates"; // Both select meeting dates
      case 8:
        return "Meeting Dates Saved"; // Meeting dates saved to database
      case 9:
        return "Sign Vessel Nomination"; // Both sign vessel nomination agreement
      case 10:
        return "Processing Inspection"; // Admin processing vessel inspection
      case 11:
        return "Sign Charter Party"; // Both sign charter party agreement
      case 12:
        return "Completed"; // All steps completed
      default:
        return "Pending";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Just now";
    if (diffInHours === 1) return "1 hour ago";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 day ago";
    return `${diffInDays} days ago`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Action handlers
  const handleMoveForward = useCallback(() => {
    console.log("handleMoveForward called with requestId:", requestId);
    
    if (!requestId) {
      showToast("Request ID is missing", "error");
      return;
    }
    
    moveForward({
      requestId: requestId,
      body: moveForwardForm
    });
  }, [requestId, moveForwardForm, moveForward, showToast]);

  const handleCloseMoveForwardModal = useCallback(() => {
    resetMoveForwardForm();
    setShowMoveForwardModal(false);
  }, [resetMoveForwardForm]);

  const submitMeetingDates = useCallback(() => {
    if (!meetingDates.date1 || !meetingDates.date2) {
      showToast("Please select both meeting dates", "error");
      return;
    }

    if (requestId) {
      selectMeetingDate({
        requestId: requestId,
        body: {
          date1: new Date(meetingDates.date1).toISOString(),
          date2: new Date(meetingDates.date2).toISOString()
        }
      });
    }
  }, [meetingDates, requestId, selectMeetingDate, showToast]);

  const confirmSignVesselAgreement = useCallback(() => {
    if (requestId) {
      signVesselAgreement({
        requestId: requestId,
        body: { signed: true }
      });
    }
  }, [requestId, signVesselAgreement]);

  const confirmSignCharterPartyAgreement = useCallback(() => {
    if (requestId) {
      signCharterPartyAgreement({
        requestId: requestId,
        body: { signed: true }
      });
    }
  }, [requestId, signCharterPartyAgreement]);

  if (charterRequestLoading) {
    return (
      <div className="charter-details-container">
        <div className="charter-details-header">
          <Skeleton height={40} width={200} />
        </div>
        <div className="charter-details-content">
          <div className="charter-details-main">
            <Skeleton height={300} style={{ marginBottom: 20 }} />
            <Skeleton height={20} count={8} style={{ marginBottom: 10 }} />
          </div>
          <div className="charter-details-sidebar">
            <Skeleton height={200} style={{ marginBottom: 20 }} />
            <Skeleton height={40} count={4} style={{ marginBottom: 10 }} />
          </div>
        </div>
      </div>
    );
  }

  if (charterRequestError) {
    return (
      <div className="charter-details-container">
        <div className="charter-details-header">
          <button onClick={() => navigate('/charterer-dashboard/ongoing-process')} className="back-button">
            <FaArrowLeft /> Back to Ongoing Process
          </button>
        </div>
        <div style={{ textAlign: "center", color: "#e74c3c", padding: "40px 0" }}>
          Error: {charterRequestError.message}
        </div>
      </div>
    );
  }

  if (!charterRequest) {
    return (
      <div className="charter-details-container">
        <div className="charter-details-header">
          <button onClick={() => navigate('/charterer-dashboard/ongoing-process')} className="back-button">
            <FaArrowLeft /> Back to Ongoing Process
          </button>
        </div>
        <div style={{ textAlign: "center", color: "#6b7280", padding: "40px 0" }}>
          Charter request not found
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="charter-details-container">
        {/* Header */}
        <div className="charter-details-header">
          <button onClick={() => navigate('/charterer-dashboard/ongoing-process')} className="back-button">
            <FaArrowLeft /> Back to Ongoing Process
          </button>
          <div className="charter-details-title">
            <h1>Charter Request #{charterRequest._id?.slice(-8)}</h1>
            <span className={`status-badge ${getStatusClass(getRequestStatus(charterRequest))}`}>
              {getRequestStatus(charterRequest)}
            </span>
          </div>
        </div>

        <div className="charter-details-content">
          {/* Main Content */}
          <div className="charter-details-main">
            {/* Workflow Steps */}
            <div className="detail-card">
              <WorkflowSteps 
                currentStep={currentStep} 
                userType="charterer" 
              />
            </div>

            {/* Overview Card */}
            <div className="detail-card">
              <h2>Request Overview</h2>
              <div className="overview-grid">
                <div className="overview-item">
                  <FaClock className="overview-icon" />
                  <div>
                    <label>Start Date</label>
                    <span>{charterRequest.startDate ? formatDate(charterRequest.startDate) : "N/A"}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaCalendar className="overview-icon" />
                  <div>
                    <label>Duration</label>
                    <span>{charterRequest.duration} days</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaUsers className="overview-icon" />
                  <div>
                    <label>Passengers</label>
                    <span>{charterRequest.numberOfPassengers || "N/A"}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaShieldAlt className="overview-icon" />
                  <div>
                    <label>Security Required</label>
                    <span>{charterRequest.security ? "Yes" : "No"}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaDollarSign className="overview-icon" />
                  <div>
                    <label>Requested Price</label>
                    <span>{formatPrice(charterRequest.pricing)}</span>
                  </div>
                </div>
                <div className="overview-item">
                  <FaDollarSign className="overview-icon" />
                  <div>
                    <label>Final Proposed Price</label>
                    <span>{formatPrice(charterRequest.finalProposedPrice)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scope of Work */}
            <div className="detail-card">
              <h2>Scope of Work</h2>
              <p className="scope-text">{charterRequest.scopeOfWork || "No scope of work specified"}</p>
            </div>

            {/* Technical Requirements */}
            <div className="detail-card">
              <h2>Technical Requirements</h2>
              <p className="requirements-text">{charterRequest.technicalRequirements || "No technical requirements specified"}</p>
            </div>

            {/* Other Requirements */}
            <div className="detail-card">
              <h2>Other Requirements</h2>
              <p className="requirements-text">{charterRequest.otherRequirements || "No other requirements specified"}</p>
            </div>

            {/* Restrictions */}
            <div className="detail-card">
              <h2>Restrictions</h2>
              <p className="requirements-text">{charterRequest.restrictionsDraft || "No restrictions specified"}</p>
            </div>

            {/* Location Information */}
            <div className="detail-card">
              <h2>Location Information</h2>
              <div className="location-info">
                {charterRequest.origin && charterRequest.destination ? (
                  <>
                    <div className="location-item">
                      <FaMapMarkerAlt className="location-icon" />
                      <div>
                        <label>Origin</label>
                        <span>{charterRequest.origin.coordinates?.join(", ") || "N/A"}</span>
                      </div>
                    </div>
                    <div className="location-item">
                      <FaMapMarkerAlt className="location-icon" />
                      <div>
                        <label>Destination</label>
                        <span>{charterRequest.destination.coordinates?.join(", ") || "N/A"}</span>
                      </div>
                    </div>
                  </>
                ) : charterRequest.locations && charterRequest.locations.length > 0 ? (
                  <div className="location-item">
                    <FaMapMarkerAlt className="location-icon" />
                    <div>
                      <label>Locations</label>
                      <span>{charterRequest.locations.map(loc => loc.coordinates?.join(", ")).join("; ")}</span>
                    </div>
                  </div>
                ) : (
                  <p>No location information available</p>
                )}
              </div>
            </div>

            {/* Request Details */}
            <div className="detail-card">
              <h2>Request Details</h2>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Extension Period Notice</label>
                  <span>{charterRequest.extensionPeriodNotice || "N/A"} days</span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{charterRequest.createdAt ? formatTimeAgo(charterRequest.createdAt) : "N/A"}</span>
                </div>
                <div className="detail-item">
                  <label>Security Pricing</label>
                  <span>{charterRequest.canProvideSecurity?.pricing ? formatPrice(charterRequest.canProvideSecurity.pricing) : "N/A"}</span>
                </div>
                <div className="detail-item">
                  <label>Request ID</label>
                  <span>{charterRequest._id}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="charter-details-sidebar">
            {/* Next Action Card - Top Right */}
            {nextAction.action ? (
              <div className="detail-card next-action-card">
                <h2>Next Action Required</h2>
                <div className="next-action-content">
                  <div className="next-action-info">
                    <h3>{nextAction.title}</h3>
                    <p>{nextAction.description}</p>
                  </div>
                  <div className="next-action-button">
                    {nextAction.action === "moveForward" && (
                      <button 
                        className="action-btn primary"
                        onClick={() => setShowMoveForwardModal(true)}
                        disabled={moveForwardLoading}
                      >
                        <FaHandshake /> Move Forward
                      </button>
                    )}
                    {nextAction.action === "selectMeetingDates" && (
                      <button 
                        className="action-btn primary"
                        onClick={() => setShowMeetingDateModal(true)}
                        disabled={meetingDateLoading}
                      >
                        <FaCalendar /> Select Meeting Dates
                      </button>
                    )}
                    {nextAction.action === "signAgreement" && (
                      <button 
                        className="action-btn primary"
                        onClick={() => setShowSignVesselModal(true)}
                        disabled={signAgreementLoading}
                      >
                        <FaFileSignature /> Sign Vessel Nomination Agreement
                      </button>
                    )}
                    {nextAction.action === "signVesselNomination" && (
                      <button 
                        className="action-btn primary"
                        onClick={() => setShowSignVesselModal(true)}
                        disabled={signAgreementLoading}
                      >
                        <FaFileSignature /> Sign Vessel Nomination Agreement
                      </button>
                    )}
                    {(nextAction.action === "signCharterParty" || nextAction.action === "signCharterPartyAgreement") && (
                      <button 
                        className="action-btn primary"
                        onClick={() => setShowSignCharterPartyModal(true)}
                        disabled={signCharterPartyAgreementLoading}
                      >
                        <FaFileSignature /> Sign Charter Party Agreement
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="detail-card next-action-card">
                <h2>Current Status</h2>
                <div className="next-action-content">
                  <div className="next-action-info">
                    <h3>{nextAction.title}</h3>
                    <p>{nextAction.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions Info Card */}
            <div className="action-card">
              <h3>Current Status</h3>
              <div className="action-buttons">
                {displayStep === 1 && (
                  <div className="no-action-message">
                    <p>Charter request created. Waiting for admin review.</p>
                  </div>
                )}
                {displayStep === 2 && (
                  <div className="no-action-message">
                    <p>Admin has approved. Waiting for vessel owner response.</p>
                  </div>
                )}
                {displayStep === 3 && (
                  <div className="no-action-message">
                    <p>Waiting for vessel owner response.</p>
                  </div>
                )}
                {displayStep === 4 && (
                  <div className="no-action-message">
                    <p>Waiting for vessel owner response.</p>
                  </div>
                )}
                {displayStep === 5 && (
                  <div className="no-action-message">
                    <p>Review and respond to vessel owner's proposal.</p>
                  </div>
                )}
                {displayStep === 6 && (
                  <div className="no-action-message">
                    <p>Waiting for admin processing.</p>
                  </div>
                )}
                {displayStep === 7 && (
                  <div className="no-action-message">
                    <p>Select your preferred meeting dates.</p>
                  </div>
                )}
                {displayStep === 8 && (
                  <div className="no-action-message">
                    <p>Sign the vessel nomination agreement.</p>
                  </div>
                )}
                {displayStep === 9 && (
                  <div className="no-action-message">
                    <p>Sign the vessel nomination agreement.</p>
                  </div>
                )}
                {displayStep === 10 && (
                  <div className="no-action-message">
                    <p>Waiting for vessel inspection result.</p>
                  </div>
                )}
                {displayStep === 11 && (
                  <div className="no-action-message">
                    <p>Sign the charter party agreement.</p>
                  </div>
                )}
                {displayStep === 12 && (
                  <div className="no-action-message">
                    <p>✓ Process Complete - All agreements have been signed!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Info */}
            <div className="info-card">
              <h3>Quick Info</h3>
              <div className="info-item">
                <label>Request ID</label>
                <span>{charterRequest._id?.slice(-8)}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`status-text ${getStatusClass(getRequestStatus(charterRequest))}`}>
                  {getRequestStatus(charterRequest)}
                </span>
              </div>
              <div className="info-item">
                <label>Vessel ID</label>
                <span>{charterRequest.vessel || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Vessel Owner</label>
                <span>{charterRequest.vesselOwner || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Duration</label>
                <span>{charterRequest.duration} days</span>
              </div>
              <div className="info-item">
                <label>Passengers</label>
                <span>{charterRequest.numberOfPassengers || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Security</label>
                <span>{charterRequest.security ? "Required" : "Not Required"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Move Forward Modal */}
      {showMoveForwardModal && (
        <div className="charter-details-modal-overlay" onClick={handleCloseMoveForwardModal}>
          <div className="charter-details-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="charter-details-modal-header">
              <h3>Respond to Vessel Owner</h3>
              <button onClick={handleCloseMoveForwardModal}>&times;</button>
            </div>
            <div className="charter-details-modal-body">
              <div className="form-group">
                <h4 style={{ marginBottom: "16px", color: "#374151", fontSize: "16px" }}>Your Decision</h4>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="processed"
                      value="true"
                      checked={moveForwardForm.processed === true}
                      onChange={() => setMoveForwardForm(prev => ({ ...prev, processed: true }))}
                    />
                    Accept and proceed with the charter request
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="processed"
                      value="false"
                      checked={moveForwardForm.processed === false}
                      onChange={() => setMoveForwardForm(prev => ({ ...prev, processed: false }))}
                    />
                    Decline the charter request
                  </label>
                </div>
              </div>

              {moveForwardForm.processed === true && (
                <div className="form-group">
                  <h4 style={{ marginBottom: "16px", color: "#374151", fontSize: "16px" }}>Price Acceptance</h4>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="acceptPrice"
                        value="true"
                        checked={moveForwardForm.acceptPrice === true}
                        onChange={() => setMoveForwardForm(prev => ({ ...prev, acceptPrice: true }))}
                      />
                      Accept the vessel owner's proposed price
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="acceptPrice"
                        value="false"
                        checked={moveForwardForm.acceptPrice === false}
                        onChange={() => setMoveForwardForm(prev => ({ ...prev, acceptPrice: false }))}
                      />
                      Proceed but negotiate the price
                    </label>
                  </div>
                </div>
              )}

              {moveForwardForm.processed === false && (
                <div className="form-group">
                  <h4 style={{ marginBottom: "16px", color: "#374151", fontSize: "16px" }}>Reason for Declining</h4>
                  <textarea
                    placeholder="Please provide a reason for declining this charter request..."
                    value={moveForwardForm.userMessage}
                    onChange={(e) => setMoveForwardForm(prev => ({ ...prev, userMessage: e.target.value }))}
                    rows={4}
                    style={{ width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", resize: "vertical" }}
                  />
                </div>
              )}

              <div className="charter-details-modal-actions">
                <button onClick={handleCloseMoveForwardModal}>Cancel</button>
                <button 
                  onClick={handleMoveForward}
                  disabled={moveForwardLoading || (moveForwardForm.processed === false && !moveForwardForm.userMessage.trim())}
                  style={{ background: moveForwardForm.processed ? "#2563EB" : "#dc2626" }}
                >
                  {moveForwardLoading ? "Processing..." : (moveForwardForm.processed ? "Accept & Proceed" : "Decline Request")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Date Modal */}
      {showMeetingDateModal && (
        <div className="charter-details-modal-overlay" onClick={() => setShowMeetingDateModal(false)}>
          <div className="charter-details-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="charter-details-modal-header">
              <h3>Select Meeting Dates</h3>
              <button onClick={() => setShowMeetingDateModal(false)}>&times;</button>
            </div>
            <div className="charter-details-modal-body">
              <p style={{ marginBottom: "20px", color: "#6B7280", fontSize: "14px" }}>
                Please select 2 preferred meeting dates from the available options below.
              </p>
              
              {charterRequest?.meetingDatesCreated ? (
                <>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequest.meetingDatesCreated.date1 || meetingDates.date2 === charterRequest.meetingDatesCreated.date1}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: charterRequest.meetingDatesCreated.date1 }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: charterRequest.meetingDatesCreated.date1 }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequest.meetingDatesCreated.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequest.meetingDatesCreated.date1) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequest.meetingDatesCreated.date1 && 
                                  meetingDates.date2 !== charterRequest.meetingDatesCreated.date1)}
                      />
                      First Meeting Date: {new Date(charterRequest.meetingDatesCreated.date1).toLocaleString()}
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequest.meetingDatesCreated.date2 || meetingDates.date2 === charterRequest.meetingDatesCreated.date2}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: charterRequest.meetingDatesCreated.date2 }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: charterRequest.meetingDatesCreated.date2 }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequest.meetingDatesCreated.date2) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequest.meetingDatesCreated.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequest.meetingDatesCreated.date2 && 
                                  meetingDates.date2 !== charterRequest.meetingDatesCreated.date2)}
                      />
                      Second Meeting Date: {new Date(charterRequest.meetingDatesCreated.date2).toLocaleString()}
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={meetingDates.date1 === charterRequest.meetingDatesCreated.date3 || meetingDates.date2 === charterRequest.meetingDatesCreated.date3}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // If date1 is empty, use it; otherwise use date2
                            if (!meetingDates.date1) {
                              setMeetingDates(prev => ({ ...prev, date1: charterRequest.meetingDatesCreated.date3 }));
                            } else if (!meetingDates.date2) {
                              setMeetingDates(prev => ({ ...prev, date2: charterRequest.meetingDatesCreated.date3 }));
                            }
                          } else {
                            // Remove from whichever slot it's in
                            if (meetingDates.date1 === charterRequest.meetingDatesCreated.date3) {
                              setMeetingDates(prev => ({ ...prev, date1: "" }));
                            } else if (meetingDates.date2 === charterRequest.meetingDatesCreated.date3) {
                              setMeetingDates(prev => ({ ...prev, date2: "" }));
                            }
                          }
                        }}
                        disabled={(meetingDates.date1 && meetingDates.date2) && 
                                 (meetingDates.date1 !== charterRequest.meetingDatesCreated.date3 && 
                                  meetingDates.date2 !== charterRequest.meetingDatesCreated.date3)}
                      />
                      Third Meeting Date: {new Date(charterRequest.meetingDatesCreated.date3).toLocaleString()}
                    </label>
                  </div>
                </>
              ) : (
                <div style={{ color: "#e74c3c", textAlign: "center", padding: "20px 0" }}>
                  No meeting dates available. Please wait for the admin to create meeting dates.
                </div>
              )}

              <div className="charter-details-modal-actions">
                <button onClick={() => setShowMeetingDateModal(false)}>Cancel</button>
                <button
                  onClick={submitMeetingDates}
                  disabled={meetingDateLoading || !meetingDates.date1 || !meetingDates.date2 || !charterRequest?.meetingDatesCreated}
                  style={{ background: "#2563EB" }}
                >
                  {meetingDateLoading ? "Submitting..." : "Submit Dates"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sign Vessel Nomination Agreement Modal */}
      <VesselNominationAgreementModal
        isOpen={showSignVesselModal}
        onClose={() => setShowSignVesselModal(false)}
        onSign={confirmSignVesselAgreement}
        isLoading={signAgreementLoading}
        charterRequest={charterRequest}
        profileData={profileData}
      />

      {/* Sign Charter Party Agreement Modal */}
      <CharterPartyAgreementModal
        isOpen={showSignCharterPartyModal}
        onClose={() => setShowSignCharterPartyModal(false)}
        onSign={confirmSignCharterPartyAgreement}
        isLoading={signCharterPartyAgreementLoading}
        charterRequest={charterRequest}
        profileData={profileData}
      />
    </>
  );
};

export default CharterRequestDetails; 